import { useApp } from "@/context/AppContext";
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from "@/components/ui/popover";
import { LAYOUTS, LANGUAGES, THEMES, THEME_GROUPS } from "@/os/appearance";
import { Palette, LayoutGrid, Languages, Check, Clock as ClockIcon } from "lucide-react";

export function AppearancePanel() {
  const { theme, setTheme, osLayout, setOsLayout, lang, setLang, clockMode, setClockMode } = useApp();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 w-8 rounded-md hover:bg-nav-chrome/10 flex items-center justify-center relative" data-testid="os-appearance" aria-label="Appearance">
          <Palette className="h-4 w-4" />
          <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-navy" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="appearance-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold tracking-[0.12em] uppercase text-muted-foreground">Appearance</span>
          <PopoverClose asChild><button className="text-xs text-navy hover:underline" data-testid="appearance-done">Done</button></PopoverClose>
        </div>
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain">
          <div className="p-4 space-y-5">
            {/* Layout */}
            <section>
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2"><LayoutGrid className="h-3.5 w-3.5" />Layout</p>
              <div className="grid grid-cols-2 gap-2">
                {LAYOUTS.map((l) => {
                  const on = osLayout === l.key;
                  return (
                    <button key={l.key} onClick={() => setOsLayout(l.key)} data-testid={`layout-${l.key}`}
                      className={"text-left rounded-xl border p-3 transition-colors " + (on ? "border-navy ring-1 ring-navy/40 bg-navy/5" : "border-border hover:bg-muted")}>
                      <p className="text-sm font-semibold">{l.name}</p>
                      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{l.desc}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Language */}
            <section>
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2"><Languages className="h-3.5 w-3.5" />Language</p>
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.map((l) => {
                  const on = lang === l.key;
                  return (
                    <button key={l.key} onClick={() => setLang(l.key)} data-testid={`lang-${l.key}`}
                      className={"rounded-xl border p-3 text-center transition-colors " + (on ? "border-navy ring-1 ring-navy/40 bg-navy/5" : "border-border hover:bg-muted")}>
                      <p className="text-sm font-semibold">{l.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{l.code}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Clock */}
            <section>
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2"><ClockIcon className="h-3.5 w-3.5" />Clock</p>
              <div className="grid grid-cols-2 gap-2">
                {[["digital", "Digital"], ["analog", "Analog"]].map(([k, l]) => {
                  const on = clockMode === k;
                  return (
                    <button key={k} onClick={() => setClockMode(k)} data-testid={`clock-${k}`}
                      className={"rounded-xl border p-3 text-center transition-colors " + (on ? "border-navy ring-1 ring-navy/40 bg-navy/5" : "border-border hover:bg-muted")}>
                      <p className="text-sm font-semibold">{l}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Time is shown in your local timezone based on your location.</p>
            </section>

            {/* Themes */}
            {THEME_GROUPS.map((group) => (
              <section key={group}>
                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">{group}</p>
                <div className="grid grid-cols-3 gap-2">
                  {THEMES.filter((t) => t.group === group).map((t) => {
                    const on = theme === t.key;
                    const darkText = t.mode === "DARK";
                    return (
                      <button key={t.key} onClick={() => setTheme(t.key)} data-testid={`theme-${t.key}`}
                        className={"relative rounded-xl p-2.5 h-[62px] flex flex-col justify-between text-left overflow-hidden border transition-transform hover:-translate-y-0.5 " + (on ? "ring-2 ring-navy border-transparent" : "border-border")}
                        style={{ background: t.swatch }}>
                        <div className="flex items-center gap-1">
                          {t.dots.map((d, i) => <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: d }} />)}
                          {on && <Check className="h-3 w-3 ml-auto" style={{ color: darkText ? "#fff" : "#1A3A5C" }} />}
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold leading-tight" style={{ color: darkText ? "#fff" : "#0D1117" }}>{t.name}</p>
                          <p className="text-[9px] tracking-wide" style={{ color: darkText ? "rgba(255,255,255,0.65)" : "rgba(13,17,23,0.55)" }}>{t.mode}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
